import { forwardRef, useEffect, useState } from 'react';
import { useDebounce } from '@uidotdev/usehooks';
import { useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { styled } from '@mui/material/styles';
import { Box, Paper } from '@mui/material';
import {
  TreeItem,
  TreeItemContentProps,
  TreeView,
  useTreeItem,
} from '@mui/x-tree-view';
import {
  Home as HomeIcon,
  AccountCircleOutlined as AccountCircleOutlinedIcon,
  AirlineSeatReclineNormal as AirlineSeatReclineNormalIcon,
  Apartment as ApartmentOutlinedIcon,
  Monitor as MonitorOutlinedIcon,
  AccountTreeOutlined as AccountTreeOutlinedIcon,
  PlaylistAddCheckOutlined as PlaylistAddCheckOutlinedIcon,
  SettingsOutlined as SettingsOutlinedIcon,
  ModeOfTravelOutlined as ModeOfTravelOutlinedIcon,
  TuneOutlined as TuneOutlinedIcon,
  PersonOutline as PersonOutlineIcon,
  ReceiptLongOutlined as ReceiptLongOutlinedIcon,
} from '@mui/icons-material';

import { useAppSelector } from '../../hooks/useAppSelector';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useCommonTranslation } from '../../hooks/useCommonTranslation';
import { usePortalName } from '../../hooks/usePortalName';
import { useFeatureReviewInvoicing } from '../../hooks/features/useFeatureReviewInvoicing';
import { useFeatureExceptionResolve } from '../../hooks/features/useFeatureExceptionResolve';
import { useCanManageMolinaUsers } from '../../hooks/permissions/useCanManageMolinaUsers';
import { useCanReadCSA } from '../../hooks/permissions/useCanReadCSA';
import { useCanReadPassengers } from '../../hooks/permissions/useCanReadPassengers';
import { useCanReadCSASupervisor } from '../../hooks/permissions/useCanReadCSASupervisor';
import { useCanReadClients } from '../../hooks/permissions/useCanReadClients';
import { useCanReadMonitoring } from '../../hooks/permissions/useCanReadMonitoring';
import { useCanReadAdministration } from '../../hooks/permissions/useCanReadAdministration';
import { useCanReadWorkQueues } from '../../hooks/permissions/useCanReadWorkQueues';
import { useCanReadTripInventory } from '../../hooks/permissions/useCanReadTripInventory';
import { useCanReadWorkFlows } from '../../hooks/permissions/useCanReadWorkFlows';
import { useIsPR2User } from '../../hooks/permissions/useIsPR2User';
import { useIsClientPortalUser } from '../../hooks/useIsClientPortalUser';
import { useIsClientPortalAdminUser } from '../../hooks/useIsClientPortalAdminUser';

import * as triplegSlice from '../../data/tripleg.slice';
import * as accountingSlice from '../../data/accounting.slice';
import * as uiSlice from '../../data/ui.slice';

const PREFIX = 'HomeNavBar';

const componentClasses = {
  content: `${PREFIX}-content`,
  item: `${PREFIX}-item`,
  itemContent: `${PREFIX}-itemContent`,
  itemIcon: `${PREFIX}-itemIcon`,
  itemLabel: `${PREFIX}-itemLabel`,
  badge: `${PREFIX}-badge`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`&.${componentClasses.content}`]: {
    borderRight: '1px solid #ccc',
    borderRadius: 0,
    whiteSpace: 'nowrap',
    height: 'fit-content',
    minHeight: `calc(100% - ${theme.spacing(7)})`,
    minWidth: 244,
    paddingTop: theme.spacing(7),
    boxShadow: '0px 6px 6px 0px rgba(1, 16, 18, 0.15)',
    '& .MuiTreeItem-group': {
      marginLeft: theme.spacing(4),
      '& .MuiTreeItem-content': {
        fontSize: 14,
        [`& .${componentClasses.itemContent}`]: {
          marginTop: 9,
          marginBottom: 9,
        },
      },
    },
  },
  [`& .${componentClasses.item}`]: {
    '& .MuiTreeItem-content': {
      padding: 0,
      opacity: 0.6,
      userSelect: 'none',
      '&:hover': {
        backgroundColor: theme.palette.background.paper,
      },
      '&:not(:hover)': {
        backgroundColor: theme.palette.background.paper,
      },
      '&:active': {
        backgroundColor: theme.palette.background.paper,
      },
      '&:focus': {
        backgroundColor: theme.palette.background.paper,
      },
    },
    '& .Mui-selected': {
      backgroundColor: theme.palette.background.paper,
      opacity: 1.0,
      [`& .${componentClasses.itemContent}`]: {
        borderRight: `${theme.spacing(0.375)} solid black`,
      },
    },
  },
  [`& .${componentClasses.itemContent}`]: {
    display: 'flex',
    marginTop: 14,
    marginBottom: 14,
    width: '100%',
    alignItems: 'center',
  },
  [`& .${componentClasses.itemIcon}`]: {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  [`& .${componentClasses.itemLabel}`]: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  [`& .${componentClasses.badge}`]: {
    minWidth: '20px',
    height: '20px',
    borderRadius: '10px',
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 600,
    padding: '0 6px',
  },
}));

const TreeItemContent = forwardRef(
  (props: TreeItemContentProps & { badgeCount?: number }, ref: any) => {
    const { classes, className, label, nodeId, icon, badgeCount } = props;

    const {
      disabled,
      expanded,
      selected,
      focused,
      handleSelection,
      handleExpansion,
      preventSelection,
    } = useTreeItem(nodeId);

    const handleMouseDown = (
      event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    ) => {
      preventSelection(event);
    };

    const handleSelectionClick = (
      event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    ) => {
      if (nodeId.startsWith('/')) {
        handleSelection(event);
      } else {
        handleExpansion(event);
      }
    };

    return (
      <Box
        className={clsx(className, classes.root, {
          [classes.expanded]: expanded,
          [classes.selected]: selected,
          [classes.focused]: focused,
          [classes.disabled]: disabled,
        })}
        onMouseDown={handleMouseDown}
        onClick={handleSelectionClick}
        ref={ref as React.Ref<HTMLDivElement>}>
        <Box className={componentClasses.itemContent}>
          <Box className={componentClasses.itemIcon}>{icon || null}</Box>
          <Box className={componentClasses.itemLabel}>
            <Box>{label}</Box>
            {badgeCount !== undefined && badgeCount > 0 && (
              <Box className={componentClasses.badge}>{badgeCount}</Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  },
);

export default function HomeNavBar() {
  const t = useCommonTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const canManageMolinaUsers = useCanManageMolinaUsers();
  const canViewCSAPage = useCanReadCSA();
  const canViewCSASupervisorPage = useCanReadCSASupervisor();
  const canViewPassengerPage = useCanReadPassengers();
  const canViewClientsPage = useCanReadClients();
  const canViewMonitoringPage = useCanReadMonitoring();
  const canViewAdministrationPage = useCanReadAdministration();
  const canViewWorkQueuesPage = useCanReadWorkQueues();
  const canViewWorkFlowsPage = useCanReadWorkFlows();
  const canViewTripInventoryPage = useCanReadTripInventory();
  const isPR2User = useIsPR2User();
  const isPortal = !!usePortalName();
  const isClientPortalUser = useIsClientPortalUser();
  const isClientPortalAdminUser = useIsClientPortalAdminUser();
  const isClientUser = isClientPortalUser || isClientPortalAdminUser;
  const enableReviewInvoicing = useFeatureReviewInvoicing();
  const enableExceptionResolve = useFeatureExceptionResolve();
  const needsReviewData = useAppSelector(accountingSlice.selectNeedsReviewData);
  const [selected, setSelected] = useState<string>(location.pathname);
  const [expanded, setExpanded] = useState<string[]>([]);

  // Get trip leg counts from Redux
  const todaysLegs = useAppSelector(triplegSlice.selectTodaysTripLegs);
  const allTripLegs = useAppSelector(triplegSlice.selectAllTripLegs);
  const discussionLegs = useAppSelector(triplegSlice.selectDiscussionTripsLegs);
  const tripLegCounts = useAppSelector(triplegSlice.selectTripLegCounts);
  const loadingTripLegCounts = useAppSelector(
    triplegSlice.selectLoadingTripLegCounts,
  );
  const loadedTripLegCounts = useAppSelector(
    triplegSlice.selectLoadedTripLegCounts,
  );
  const tripLegEventEmitted = useAppSelector(
    triplegSlice.selectTripLegEventEmitted,
  );
  const errorMessage = useAppSelector(uiSlice.selectErrorMessage);

  interface NavNode {
    text: string;
    icon: any;
    url: string;
    visible: boolean;
    children?: NavNode[];
    badgeCount?: number;
  }

  const todaysTripsCount = tripLegCounts?.today ?? todaysLegs?.itemCount ?? 0;
  const allTripsCount = tripLegCounts?.all ?? allTripLegs?.itemCount ?? 0;
  const discussionTripsCount =
    tripLegCounts?.discuss ?? discussionLegs?.itemCount ?? 0;

  useEffect(() => {
    if (
      canViewTripInventoryPage &&
      !loadingTripLegCounts &&
      !loadedTripLegCounts &&
      !errorMessage
    ) {
      dispatch(triplegSlice.fetchTripLegCountsAsync());
    }
  }, [
    canViewTripInventoryPage,
    loadingTripLegCounts,
    loadedTripLegCounts,
    errorMessage,
  ]);

  const [refetchTripLegCounts, setRefetchTripLegCounts] = useState(false);
  const debouncedRefetchTripLegCounts = useDebounce(refetchTripLegCounts, 200);

  useEffect(() => {
    if (canViewTripInventoryPage && tripLegEventEmitted) {
      setRefetchTripLegCounts(true);
    }
  }, [tripLegEventEmitted, canViewTripInventoryPage]);

  useEffect(() => {
    if (debouncedRefetchTripLegCounts) {
      dispatch(triplegSlice.fetchTripLegCountsAsync());
      setRefetchTripLegCounts(false);
    }
  }, [debouncedRefetchTripLegCounts]);

  const navItems: NavNode[] = [
    {
      text: t('navigation.home'),
      icon: <HomeIcon />,
      url: '/home',
      visible: canViewCSAPage || isPR2User,
    },
    // If the user is both a CSA and a CSA supervisor, then they get a CSA home page as /home
    // and a /csa-supervisor page right below that. If they are just a CSA supervisor then
    // the supervisor page becomes the /home page.
    {
      text: t('navigation.CSASupervisor'),
      icon: <AccountCircleOutlinedIcon />,
      url: '/csa-supervisor',
      visible: canViewCSASupervisorPage,
    },
    {
      text: t('navigation.passengers'),
      icon: <AirlineSeatReclineNormalIcon />,
      url: '/passengers',
      visible: canViewPassengerPage,
    },
    {
      text: t('navigation.clients'),
      icon: <ApartmentOutlinedIcon />,
      url: '/clients',
      visible: canViewClientsPage,
    },
    {
      text: t('navigation.monitorTrips'),
      icon: <MonitorOutlinedIcon />,
      url: 'Monitoring',
      visible: canViewMonitoringPage,
      children: [
        {
          text: t('navigation.todaysTrips'),
          icon: '',
          url: '/monitoring?tab=todaysTrips',
          visible: true,
        },
        {
          text: t('navigation.exceptions'),
          icon: '',
          url: enableExceptionResolve
            ? '/monitoring/exceptions'
            : '/monitoring?tab=exceptions',
          visible: true,
        },
      ],
    },
    {
      text: t('navigation.settings'),
      icon: <SettingsOutlinedIcon />,
      url: '/settings',
      visible: canViewAdministrationPage,
    },
    {
      text: t('navigation.workQueues'),
      icon: <PlaylistAddCheckOutlinedIcon />,
      url: '/workQueues',
      visible: canViewWorkQueuesPage,
    },
    {
      text: t('navigation.workflows'),
      icon: <AccountTreeOutlinedIcon />,
      url: '/workflows',
      visible: canViewWorkFlowsPage,
    },
    {
      text: t('navigation.tripManagement'),
      icon: <ModeOfTravelOutlinedIcon />,
      url: 'TripManagement',
      visible: canViewTripInventoryPage,
      children: [
        {
          text: t('navigation.todaysTrips'),
          icon: '',
          url: '/today',
          visible: canViewTripInventoryPage,
          badgeCount: todaysTripsCount,
        },
        {
          text: t('navigation.allTrips'),
          icon: '',
          url: '/triplegs',
          visible: canViewTripInventoryPage,
          badgeCount: allTripsCount,
        },
        {
          text: t('navigation.discuss'),
          icon: '',
          url: '/discussions',
          visible: isPR2User,
          badgeCount: discussionTripsCount,
        },
        {
          text: t('navigation.reviewInvoices'),
          icon: '',
          url: '/review-invoices',
          visible: isPR2User && enableReviewInvoicing,
          badgeCount: needsReviewData?.totalLineItems || 0,
        },
      ],
    },
    {
      text: t('navigation.invoices'),
      icon: <ReceiptLongOutlinedIcon />,
      url: '/invoices',
      visible: isPR2User && enableReviewInvoicing,
    },
    // Client Portal
    {
      text: t('navigation.members'),
      icon: <PersonOutlineIcon />,
      url: '/passengers',
      visible: isPortal && isClientUser,
    },
    {
      text: t('navigation.settings'),
      icon: <TuneOutlinedIcon />,
      url: '/settings',
      visible: isPortal && isClientPortalAdminUser,
    },
  ];

  const isCurrentRoute = (path: string, url: string): boolean => {
    return path.indexOf(url) >= 0;
  };

  const findAllAncestors = (
    items: NavNode[],
    selectedUrl: string,
    currentPath: NavNode[] = [],
  ): NavNode[] => {
    for (const item of items) {
      const newPath = [...currentPath, item];

      if (item.children) {
        const hasMatchingChild = item.children.some(
          (child) =>
            child.url === selectedUrl || selectedUrl.includes(child.url),
        );
        if (hasMatchingChild) {
          return newPath;
        }

        const foundInChildren = findAllAncestors(
          item.children,
          selectedUrl,
          newPath,
        );
        if (foundInChildren.length > 0) {
          return foundInChildren;
        }
      }
    }
    return [];
  };

  // For now assume if they are a molina user admin then no need to navigate so don't add the left nav
  if (canManageMolinaUsers) {
    return null;
  }

  const selectRoute = (evt: any, url: string) => {
    let node = navItems.find((ni) => ni.url === url);
    if (!node) {
      node = navItems.find((ni: any) =>
        ni?.children?.some((c: any) => c?.url?.includes(url)),
      );
    }

    if (!node?.children) {
      setExpanded([]);
    }
    setSelected(url);
    navigate(url);
  };

  const handleToggle = (evt: any, nodeIds: string[]) => {
    const ids = nodeIds.length ? nodeIds : expanded;

    const firstChild = navItems.find((item) => item.url === ids[0])
      ?.children?.[0];

    if (firstChild) {
      setSelected(firstChild.url);
      navigate(firstChild.url);
    }

    setExpanded(nodeIds.length ? [nodeIds[0]] : []);
  };

  const renderNode = (node: NavNode) => {
    if (node.visible) {
      const current = isCurrentRoute(location.pathname, node.url);
      return (
        <TreeItem
          key={node.url}
          label={node.text}
          ContentComponent={TreeItemContent}
          ContentProps={{ badgeCount: node.badgeCount } as any}
          nodeId={node.url}
          icon={node.icon}
          className={componentClasses.item}>
          {node.children && node.children.map((child) => renderNode(child))}
        </TreeItem>
      );
    }
  };

  useEffect(() => {
    let path = location.pathname;
    let query = location.search;
    if (path.endsWith('/')) {
      path = path.substring(0, path.length - 1);
    }
    setSelected(path + query);
  }, [location]);

  useEffect(() => {
    if (selected) {
      const ancestors = findAllAncestors(navItems, selected);
      if (ancestors.length > 0) {
        setExpanded(ancestors.map((ancestor) => ancestor.url));
      } else {
        setExpanded([]);
      }
    }
  }, [selected]);

  return (
    <StyledPaper className={componentClasses.content} elevation={2}>
      <TreeView
        selected={selected}
        onNodeSelect={selectRoute}
        onNodeToggle={handleToggle}
        expanded={expanded}>
        {navItems.map((node) => renderNode(node))}
      </TreeView>
    </StyledPaper>
  );
}
